// Shared API client. Uses cookie-based session auth (preferred) with a
// localStorage Basic Auth fallback for legacy clients/extensions.

const APP_SETTINGS_KEY = "appSettings";
const CSRF_COOKIE = "bidrl_csrf";

export function readAppSettings() {
  try {
    return JSON.parse(localStorage.getItem(APP_SETTINGS_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

export function writeAppSettings(settings) {
  localStorage.setItem(APP_SETTINGS_KEY, JSON.stringify(settings || {}));
}

export function clearAppSettings() {
  localStorage.removeItem(APP_SETTINGS_KEY);
}

export function readCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(name + "="));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : "";
}

// Header used by chrome extension and as a fallback when cookie auth fails.
// The shared dashboard now relies on cookies; this only fires if creds happen
// to be present in localStorage (e.g. transitional state).
export function getAuthHeaders() {
  const { username, password } = readAppSettings();
  if (!username || !password) return {};
  return { Authorization: "Basic " + btoa(`${username}:${password}`) };
}

export class ApiError extends Error {
  constructor(message, { status = 0, body = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

let onUnauthorized = null;

/**
 * Register a callback fired when any apiFetch returns 401 (e.g. session expired).
 * Returns an unsubscribe function.
 */
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
  return () => {
    if (onUnauthorized === fn) onUnauthorized = null;
  };
}

/**
 * Fetch a JSON API endpoint.
 *
 * @param {string} path
 * @param {object} [options]
 * @param {string} [options.method="GET"]
 * @param {object|null} [options.json]
 * @param {object|null} [options.headers]
 * @param {AbortSignal|null} [options.signal]
 * @param {boolean} [options.auth=true]   include Basic-Auth fallback header
 * @param {boolean} [options.csrf=true]   attach X-CSRF-Token for state-changing requests
 * @returns {Promise<any>}
 */
export async function apiFetch(path, options = {}) {
  const {
    method = "GET",
    json = null,
    headers = {},
    signal = null,
    auth = true,
    csrf = true,
  } = options;

  const finalHeaders = { ...headers };
  if (json !== null && json !== undefined) {
    finalHeaders["Content-Type"] = finalHeaders["Content-Type"] || "application/json";
  }
  if (auth) Object.assign(finalHeaders, getAuthHeaders());

  const stateChanging = method !== "GET" && method !== "HEAD";
  if (csrf && stateChanging) {
    const token = readCookie(CSRF_COOKIE);
    if (token) finalHeaders["X-CSRF-Token"] = token;
  }

  let res;
  try {
    res = await fetch(path, {
      method,
      headers: finalHeaders,
      body: json !== null && json !== undefined ? JSON.stringify(json) : undefined,
      signal,
      credentials: "include", // critical: send cookies
    });
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    throw new ApiError(`Network error calling ${path}`, { status: 0 });
  }

  let body = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (res.status === 401 && onUnauthorized) {
    try { onUnauthorized(); } catch { /* ignore */ }
  }

  if (!res.ok) {
    const msg =
      (body && typeof body === "object" && (body.error || body.message)) ||
      `HTTP ${res.status}`;
    throw new ApiError(msg, { status: res.status, body });
  }

  return body;
}

export const apiGet = (path, opts = {}) => apiFetch(path, { ...opts, method: "GET" });
export const apiPost = (path, json, opts = {}) =>
  apiFetch(path, { ...opts, method: "POST", json });
export const apiPatch = (path, json, opts = {}) =>
  apiFetch(path, { ...opts, method: "PATCH", json });
export const apiDelete = (path, opts = {}) =>
  apiFetch(path, { ...opts, method: "DELETE" });

// ---- Helpers for paginated list endpoints ----

/**
 * Build a query string from a plain object, dropping undefined / "" values.
 */
export function qs(params = {}) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const x of v) sp.append(k, String(x));
    } else {
      sp.append(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/**
 * Fetch a paginated endpoint. Returns { items, total, page, page_size }.
 * Tolerates legacy endpoints that return a bare array.
 */
export async function apiList(path, params = {}, opts = {}) {
  const url = `${path}${qs(params)}`;
  const data = await apiGet(url, opts);
  if (Array.isArray(data)) {
    return { items: data, total: data.length, page: 1, page_size: data.length };
  }
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? 1),
    page_size: Number(data?.page_size ?? params.page_size ?? 50),
    raw: data,
  };
}
